import React, {
	useState,
	useRef,
	useEffect,
	useMemo,
	useCallback,
} from "react";
import classes from "./Messages.module.css";
import { Button, Modal } from "antd";
import DefaultInput from "../../components/form/DefaultInput";
import { AgGridReact } from "ag-grid-react";
import { ReactComponent as SearchIcon } from "../../assets/search-find.svg";
import { ReactComponent as AddIcon } from "../../assets/add-plus.svg";
import { ReactComponent as RemoveIcon } from "../../assets/delete-trash-alt.svg";
import { ColDef } from "ag-grid-community";
import PaginationTable from "../../components/Pagination";
import TextArea from "antd/es/input/TextArea";
import api from "../../utils/axios";
import { toast } from "react-toastify";
import { valueDateFormatter } from "../../utils/formatters";
import { getUserRoleFromToken } from "../../utils/Auth";
import { ReactComponent as DeleteIcon } from "../../assets/deleteIcon.svg";
import Buttons from "../../components/form/Buttons";
import MultiModalSelector from "../../components/form/MultiModalSelector";
import { userRoles } from "../../types/enums";
import { canFetchAllUsers } from "../../lib/UserApi";
import useLang from "../../hooks/useLang";
import { usePagedList } from "../../hooks/usePagedList";
import { useMediaQuery } from "@mui/material";

type User = {
	id: number;
	user: string;
	userRoleId: number;
};

const Messages = () => {
	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [open, setOpen] = useState<boolean>(false);
	const [code, setCode] = useState<number>();
	const [selectedRowCount, setSelectedRowCount] = useState(0);
	const [title, setTitle] = useState<string>("");
	const [message, setMessage] = useState<string>("");
	const [allUsers, setAllUsers] = useState<any>();
	const [userOptions, setUserOptions] = useState<User[]>([]);
	const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
	const [messageIds, setMessageIds] = useState<any>([]);
	const [clientUserOptions, setClientUserOptions] = useState<User[]>([]);
	const [isAdmin, setIsAdmin] = useState<boolean>();
	const [selectedRowData, setSelectedRowData] = useState<any>([]);
	const [isModalVisible, setIsModalVisible] = useState(false);

	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);

	let grid = useRef<AgGridReact | null>(null);

	const getUsersData = async () => {
		if (!canFetchAllUsers()) return null;
		try {
			const response = await api.get("User/message-recipients");
			if (response.status === 200) {
				setAllUsers(response?.data);
			}
			return response.data;
		} catch (error) {
			console.error("Error fetching users:", error);
			return null;
		}
	};

	const filterAndMapUsers = (users: any) => {
		return users.map((item: any) => ({
			id: item.id,
			user: `${item.username} -(${item.uniqueSellerIdentifier ?? "-"})`,
			sellerId: item.sellerId,
			userRole: item.userRoleDescription,
		}));
	};

	const getAllUsers = useCallback(async () => {
		const users = await getUsersData();
		if (users) {
			const userOptions = filterAndMapUsers(users);
			setUserOptions(userOptions);
			const options = userOptions.filter(
				(elem: User) => elem.userRoleId === userRoles.ADMIN
			);
			setClientUserOptions(options);
		}
	}, []);

	const onMessageReceiversSelected = (rows: any[]) => {
		setSelectedUsers(rows);
	};
	const onMssageReceiverDeleted = () => {
		setSelectedUsers([]);
	};

	const isSmallScreen = useMediaQuery("(max-width: 994px)");

	useEffect(() => {
		const userRoleId = getUserRoleFromToken();
		userRoleId === userRoles.ADMIN ? setIsAdmin(true) : setIsAdmin(false);

		setTableColumns([
			{
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 80,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "title",
				headerName: "Subjekti",
				cellStyle: (params: any) => ({
					fontSize: "15px",
					color: "blue",
				}),
				width: 200,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "senderName",
				headerName: "Derguar Nga:",
				width: 200,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "receiverName",
				headerName: "Pranuar Nga:",
				width: 200,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "openedDate",
				headerName: "Data e Hapjes",
				valueFormatter: (params: any) =>
					valueDateFormatter(params.value),
				width: 200,
				suppressSizeToFit: isSmallScreen,
			},
			{
				field: "registeredDate",
				headerName: "Data e krijimit",
				valueFormatter: (params: any) =>
					valueDateFormatter(params.value),
				width: 200,
				suppressSizeToFit: isSmallScreen,
			},
		]);
		getAllUsers();
	}, [getAllUsers, setTableColumns]);

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const messageParams = useMemo(
		() => ({ currentPage, pageSize }),
		[currentPage, pageSize]
	);
	const messages = usePagedList<User>(
		"Message/message-select",
		messageParams,
		setCurrentPage
	);

	// New rows drop the grid's selection, so drop the ids Delete would send as well.
	useEffect(() => {
		setMessageIds([]);
		setSelectedRowCount(0);
	}, [messages.items]);

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handlePageSizeChange = (current: number, size: number) => {
		setPageSize(size);
		setCurrentPage(1);
	};

	// The code search filters the page on screen, as before.
	const filteredData = useMemo(() => {
		return messages.items.filter((item: any) => {
			return !code || item.id.toString().includes(code);
		});
	}, [code, messages.items]);

	const onSelectionChanged = () => {
		const selectedRows = grid.current?.api?.getSelectedRows();
		const selectedIds = selectedRows?.map((item: any) => ({ id: item.id }));
		setMessageIds(selectedIds);
		setSelectedRowCount(selectedRows ? selectedRows.length : 0);
	};

	const insertMessage = async () => {
		try {
			if (
				(selectedUsers?.length === 0 && isAdmin) ||
				!title ||
				!message
			) {
				toast.error("Ju lutem plotësoni të gjitha fushat.");
				return;
			}

			const data = {
				subjectTo: selectedUsers.map((elem) => elem.id) ?? 1,
				title: title,
				message: message,
			};
			console.log(data, "data");
			const response = await api.post("Message/message-insert", data);
			if (response.status === 200) {
				await messages.reload();
				cleanMessageState();
				setOpen(false);
				toast.success("Mesazhi u dergu me sukses");
			}
		} catch (error: any) {
			console.error("An error occurred: " + error.response.data);
		}
	};

	const deleteMessages = async () => {
		try {
			const response = await api.delete("Message/message-delete", {
				data: messageIds,
			});
			if (response.status === 200) {
				await messages.reload();
				setIsModalVisible(false);
				toast.success("Mesazhet u fshine me sukses");
			}
		} catch (error: any) {
			console.log(error.response.data);
		}
	};

	const onCellClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "title":
				await onTitleClick(params?.data);
				openEditModal(params);
				break;
		}
	};

	const onTitleClick = async (data: any) => {
		try {
			const response = await api.put("Message/message-update", null, {
				params: { id: data?.id },
			});
			if (response.status === 200) {
				await messages.reload();
			}
		} catch (error: any) {
			console.log(error.response.data);
		}
	};

	const openEditModal = (rowData: any) => {
		setSelectedRowData(rowData?.data);
		setIsModalVisible(true);
	};

	const formattedRegisteredDate = valueDateFormatter(
		selectedRowData?.registeredDate
	);
	const cleanMessageState = () => {
		setTitle("");
		setMessage("");
		setSelectedUsers([]);
	};
	const { strings } = useLang("messages");
	return (
		<>
			<div className={classes.mainContainer}>
				<div className={classes.heroContainer}>
					<div className={classes.firstContainer}>
						<h1 className={classes.title}>
							{strings?.mainTitle}
							<span className={classes.clientsCount}>
								{strings?.clientsCount}
							</span>
						</h1>
						<div className={classes.buttons}>
							<Button
								className={classes.button}
								onClick={() => setOpen(true)}
							>
								<AddIcon />
								{strings?.createNewMessageButton}
							</Button>
						</div>
					</div>
					<div className={classes.inputs}>
						<DefaultInput
							label={strings?.searchLabel}
							className={classes.input}
							placeholder={strings?.searchPlaceholder}
							prefix={<SearchIcon />}
							value={code}
							onChange={(e) => {
								setCurrentPage(1);
								setCode(e.target.value);
							}}
							allowClear
						/>
						<div className={classes.selectedMessagesDiv}>
							<p>
								{selectedRowCount}{" "}
								{strings?.selectedMessagesText}
							</p>
							<Button
								className={classes.buttonRemove}
								disabled={selectedRowCount === 0}
								onClick={deleteMessages}
							>
								<RemoveIcon />
								{strings?.deleteButton}
							</Button>
						</div>
					</div>
					<div
						className="ag-theme-alpine"
						style={{ height: 600, width: "100%" }}
					>
						<AgGridReact
							rowData={filteredData}
							columnDefs={tableColumns}
							defaultColDef={defaultColDef}
							undoRedoCellEditing={true}
							undoRedoCellEditingLimit={20}
							suppressCopyRowsToClipboard={true}
							defaultExcelExportParams={
								defaultExcelExportSettings
							}
							rowSelection="multiple"
							ref={grid}
							enableRangeSelection={true}
							cacheQuickFilter={true}
							onCellClicked={onCellClicked}
							onSelectionChanged={onSelectionChanged}
						/>
					</div>
					<div className={classes.pagination}>
						<PaginationTable
							current={currentPage}
							pageSize={pageSize}
							total={messages.totalCount}
							startOrderIndex={messages.firstRow}
							endOrderIndex={messages.lastRow}
							onChange={handlePageChange}
							onShowSizeChange={handlePageSizeChange}
						/>
					</div>
				</div>
			</div>
			<Modal
				open={open}
				title={strings?.newMessageModalTitle}
				centered
				onCancel={() => {
					setOpen(false);
					setTitle("");
					setMessage("");
					setSelectedUsers([]);
				}}
				width={650}
				footer={(_, { OkBtn, CancelBtn }) => (
					<>
						<div className="flex justify-end items-end w-full h-16">
							<Button
								type="primary"
								size="large"
								className="bg-blue-700"
								onClick={insertMessage}
							>
								{strings?.sendMessageButton}
							</Button>
						</div>
					</>
				)}
			>
				<div className="flex flex-col gap-4">
					{isAdmin === true ? (
						<MultiModalSelector
							primaryKey="value"
							tableData={
								isAdmin ? userOptions : clientUserOptions
							}
							buttonLabel={strings?.receiverLabel}
							buttonDesc={strings?.receiverPlaceholder}
							buttonDescAll="All"
							onRowsSelected={onMessageReceiversSelected}
							onRowsCleared={onMssageReceiverDeleted}
							value={selectedUsers}
							selectorClassName={classes.registerInput}
							inputName="Select receiver"
							disabled={false}
						/>
					) : (
						<></>
					)}

					<DefaultInput
						className={classes.registerInput}
						label={strings?.subjectLabel}
						placeholder={strings?.subjectPlaceholder}
						value={title}
						onChange={(e) => setTitle(e.target.value)}
					/>
					<div>
						<p>{strings?.messageLabel}</p>
						<TextArea
							rows={6}
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							placeholder={strings?.messagePlaceholder}
						/>
					</div>
				</div>
			</Modal>
			<Modal
				open={isModalVisible}
				width={1000}
				title={selectedRowData?.title}
				onCancel={() => setIsModalVisible(false)}
				footer={null}
			>
				<div className={classes.mainModalContainer}>
					<div className={classes.firstModalContainer}>
						<p className="font-inter font-semibold text-base leading-6 text-gray-700">
							{selectedRowData?.title}
						</p>
						<p>{selectedRowData?.message}</p>
						<div className="flex items-end h-full">
							<Button
								className={classes.deleteButtonModal}
								onClick={deleteMessages}
							>
								<DeleteIcon /> {strings?.deleteButton}
							</Button>
						</div>
					</div>
					<div className={classes.secondModalContainer}>
						<div className="flex flex-col gap-2">
							<p className="font-inter font-normal text-base leading-snug text-gray-800">
								{strings?.creationDateLabel}
							</p>
							<p className="font-inter font-bold text-base leading-5 text-gray-900">
								{formattedRegisteredDate}
							</p>
							<p className="font-inter font-normal text-base leading-snug text-gray-800">
								{strings?.lastUpdateLabel}
							</p>
							<p className="font-inter font-bold text-base leading-5 text-gray-900">
								{selectedRowData?.openedDate}
							</p>
						</div>

						<Buttons
							button1Text={strings?.closeModalButton}
							button2Text={strings?.sendModalMessageButton}
							onButton1Click={() => setIsModalVisible(false)}
							onButton2Click={() => {
								setIsModalVisible(false);
								setOpen(true);
							}}
						/>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default Messages;
